/**
 * Lo vendido sin stock que todavia espera la compra.
 *
 * QUE PREGUNTA CONTESTA
 * ---------------------
 * "¿Este margen es de verdad?"
 *
 * Una venta sin stock sale con el costo que tenia la ficha ese dia, que puede
 * estar viejo. Cuando se carga la factura que la repone, el costo de esa
 * factura reemplaza al estimado y la ganancia queda firme (0106). Mientras
 * tanto el margen de arriba incluye un costo que no es el real, y esto lo dice.
 * No depende del periodo: es lo que falta hoy.
 *
 * Lo vendido a pedido es igual hasta que llega (0111): el encargo tiene el
 * costo de la ficha. No aparecia aca y su ganancia se leia como firme (0125).
 *
 * Lo vendido con costo 0 (una ficha sin costo cargado) va en un aviso aparte
 * (0121_3): no es un costo viejo que se corrige solo cuando llega la compra,
 * es un costo que falta, y el precio entero se esta contando como ganancia.
 * Antes no aparecia en ningun lado. Esas si son del periodo elegido: una venta
 * emitida no cambia de costo, y avisar en agosto por una de septiembre decia
 * que el margen de agosto estaba inflado cuando no lo estaba.
 */
import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { aNumero, type FilaVentaCostoEstimado } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

export function CostosEstimados({
  filas,
}: {
  filas: ReadonlyArray<FilaVentaCostoEstimado>;
}): JSX.Element | null {
  const sinCosto = filas.filter((f) => f.origen === "SIN_COSTO");
  const estimados = filas.filter((f) => f.origen !== "SIN_COSTO");
  if (filas.length === 0) return null;
  if (estimados.length === 0) return <SinCosto filas={sinCosto} />;
  if (sinCosto.length === 0) return <Estimados filas={estimados} />;
  return (
    <div className="space-y-2">
      <SinCosto filas={sinCosto} />
      <Estimados filas={estimados} />
    </div>
  );
}

function SinCosto({ filas }: { filas: ReadonlyArray<FilaVentaCostoEstimado> }): JSX.Element {
  const unidades = filas.reduce((t, f) => t + aNumero(f.cantidad), 0);
  return (
    <Aviso
      tono="destructive"
      titulo={
        unidades === 1
          ? "1 unidad vendida no tiene costo cargado"
          : `${unidades} unidades vendidas no tienen costo cargado`
      }
    >
      <p>
        Se vendieron en este período con costo $ 0, así que su precio entero cuenta como ganancia y
        el margen de arriba está inflado. Una venta ya emitida no cambia de costo: estas quedan así.
        Cargá el costo en la ficha para que las próximas salgan bien.
      </p>
      <ul className="mt-2 space-y-1">
        {filas.map((f) => (
          <li key={`${f.origen ?? ""}-${f.id}`} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">{f.nombre}</span>
            <span className="text-muted-foreground">
              × {aNumero(f.cantidad)} · {f.numero} · {formatearFecha(f.fecha)} · sin costo cargado
            </span>
          </li>
        ))}
      </ul>
    </Aviso>
  );
}

function Estimados({ filas }: { filas: ReadonlyArray<FilaVentaCostoEstimado> }): JSX.Element {
  const unidades = filas.reduce((t, f) => t + aNumero(f.cantidad), 0);

  return (
    <Aviso
      tono="warning"
      titulo={
        unidades === 1
          ? "1 unidad vendida tiene el costo estimado"
          : `${unidades} unidades vendidas tienen el costo estimado`
      }
    >
      <p>
        Se vendieron antes de que entraran —sin stock o a pedido—, con el costo que tenía la ficha.
        Cuando cargues la compra que las repone o marques el encargo como recibido, toman el costo
        real y su ganancia queda firme.
      </p>
      <ul className="mt-2 space-y-1">
        {filas.map((f) => (
          <li key={`${f.origen ?? ""}-${f.id}`} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">{f.nombre}</span>
            {f.origen === "A_PEDIDO" ? (
              <span className="text-muted-foreground">· a pedido</span>
            ) : null}
            <span className="text-muted-foreground">
              × {aNumero(f.cantidad)} · {f.numero} · {formatearFecha(f.fecha)}
            </span>
            {f.costo_estimado !== null && f.moneda_estimada !== null ? (
              <span className="text-muted-foreground">
                · estimado{" "}
                <Importe monto={aNumero(f.costo_estimado)} moneda={f.moneda_estimada} tamano="sm" />
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </Aviso>
  );
}
